import { useContext, memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import DataTable from '../../../DataTable/datatable.component';
import DateColumn from '../../../DataTable/datatable.datecolumn';
import SelectFilter from '../../../DataTable/datatable.selectfilter';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';

export interface LabResultsByTimeTableProps {
    isRailCarSite?: boolean,
    showSampleGrade?: boolean,
    children?: ReactNode;
}

const LabResultsByTimeTable = memo(function LabResultsByTimeTable({ isRailCarSite = false, showSampleGrade = false, children }: LabResultsByTimeTableProps) {

    const { t } = useTranslation();
    const { lotDetail: { labResults } } = useContext(LotDetailContext);

    const GetAccessorFromQualityName = (qualityName: string) => {
        return qualityName.replace('.', '_').replace(' ', '_');
    };

    let usedLabResultsWithQualities = labResults.sort((a, b) => a.qualityDisplayOrder - b.qualityDisplayOrder).filter(l => l.quality != null && l.quality !== "" && l.usage !== "comment");
    let labResultQualities = [...new Set(usedLabResultsWithQualities.map(l => l?.quality as string))];
    let labResultsPivotMap = new Map();

    usedLabResultsWithQualities.forEach(l => {
        let row = null;
        if (labResultsPivotMap.has(l.smSampleId)) {
            row = labResultsPivotMap.get(l.smSampleId)
        } else {
            row = {
                smSampleId: l.smSampleId,
                sampleTimeUtc: l.sampleTimeUtc,
                authorisedTimeUtc: l.authorisedTimeUtc,
                batchName: l.batchName,
                unitArea: l.unitArea,
                samplingPoint: l.samplingPoint,
                grade: l.grade,
            }
        }

        let accessor = GetAccessorFromQualityName(l.quality as string);

        row[accessor] = l;

        labResultsPivotMap.set(l.smSampleId, row);
    });

    let labResultsPivot = Array.from(labResultsPivotMap.values());

    const tableColumns = [
        DateColumn({ header: t('LotDetails.LabResults.SampleTime'), accessor: 'sampleTimeUtc', width: "10%" }),
        ...isRailCarSite
            ? [
                DateColumn({ header: t('LotDetails.LabResults.AuthorisedTime'), accessor: 'authorisedTimeUtc', width:"10%" }),
                {
                    Header: t('LotDetails.LabResults.BatchName'),
                    accessor: 'batchName',
                    Filter: SelectFilter
                },
                {
                    Header: t('LotDetails.LabResults.UnitArea'),
                    accessor: 'unitArea',
                    Filter: SelectFilter
                }
            ]
            : [],
        {
            Header: t('LotDetails.LabResults.SMSampleId'),
            accessor: 'smSampleId',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.LabResults.SamplingPoint'),
            accessor: 'samplingPoint',
            filter: 'includesString'
        },
        ...showSampleGrade ? [{
            Header: t('Grade'),
            accessor: 'grade',
            Filter: SelectFilter
        }] : [],
        ...labResultQualities?.map(q => ({
            Header: q,
            accessor: GetAccessorFromQualityName(q),
            Cell: (props: any) => (props.value?.value),
            disableFilters: true
        }))
    ];

    const getCellProps = (cell: Cell<any>) => {

        if (!labResultQualities.find(q => GetAccessorFromQualityName(q) === cell.column.id)) {
            return {};
        }

        if (!cell.value) {
            return {};
        }

        const classNames: string[] = [];
        if (!cell.value.usedInCalc) {
            classNames.push("unused");
        }

        if (cell.value.isOffspec) {
            classNames.push("offspec");
        }

        if (classNames.length > 0) {
            return { className: classNames.join(' ') };
        }

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'sampleTimeUtc',
                desc: false
            },
            {
                id: 'quality',
                desc: false
            },
            {
                id: 'collection',
                desc: false
            }
        ]
    };

    return labResults.length > 0
        ? <DataTable tableColumns={tableColumns} showPaging={false} tableData={labResultsPivot} initialState={initialState} autoResetPage={false} getCellProps={getCellProps}>
            {children}
        </DataTable>
        : <>
            <p>{t<string>('CommonResponses.NoData')}</p>
            {children}
        </>
        ;
});

export default LabResultsByTimeTable;